/**
 * Granularity Toggle Composable
 * Allows switching between different data aggregation modes for analytics charts
 */

// Granularity mode constants
export const GRANULARITY_MODES = {
    TOTAL: 'total',           // Sum for the time period (default)
    PER_SECOND: 'per_second', // Average per second (TPS-style)
    PER_PERIOD: 'per_period'  // Average per interval (hour/day/week)
};

// Display labels for each mode
export const GRANULARITY_LABELS = {
    [GRANULARITY_MODES.TOTAL]: 'Total',
    [GRANULARITY_MODES.PER_SECOND]: 'Per Second',
    [GRANULARITY_MODES.PER_PERIOD]: 'Per Period'
};

// Short labels for UI toggle
export const GRANULARITY_SHORT_LABELS = {
    [GRANULARITY_MODES.TOTAL]: 'Total',
    [GRANULARITY_MODES.PER_SECOND]: '/sec',
    [GRANULARITY_MODES.PER_PERIOD]: '/period'
};

/**
 * Get the number of seconds in an interval
 * @param {string} interval - The data interval ('1m', '1h', '1d')
 * @returns {number} - Seconds in the interval
 */
export function getIntervalSeconds(interval) {
    const intervals = {
        '1m': 60,
        '5m': 300,
        '15m': 900,
        '1h': 3600,
        '4h': 14400,
        '1d': 86400,
        '1w': 604800
    };
    return intervals[interval] || 3600; // Default to 1h
}

/**
 * Get the period label for display
 * @param {string} interval - The data interval
 * @returns {string} - Human-readable period label
 */
export function getPeriodLabel(interval) {
    const labels = {
        '1m': 'minute',
        '5m': '5 min',
        '15m': '15 min',
        '1h': 'hour',
        '4h': '4 hours',
        '1d': 'day',
        '1w': 'week'
    };
    return labels[interval] || 'period';
}

/**
 * Transform a value based on granularity mode
 * @param {number} value - The raw total value
 * @param {string} mode - The granularity mode
 * @param {string} interval - The data interval
 * @returns {number} - Transformed value
 */
export function transformValue(value, mode, interval) {
    if (value === null || value === undefined || isNaN(value)) {
        return 0;
    }

    const seconds = getIntervalSeconds(interval);

    switch (mode) {
        case GRANULARITY_MODES.PER_SECOND:
            return value / seconds;
        case GRANULARITY_MODES.PER_PERIOD:
            // Per period is the raw value (already per interval)
            return value;
        case GRANULARITY_MODES.TOTAL:
        default:
            return value;
    }
}

/**
 * Get the appropriate unit suffix for a metric
 * @param {string} mode - The granularity mode
 * @param {string} interval - The data interval
 * @param {string} baseUnit - The base unit (e.g., 'tx', 'SOL', 'CU')
 * @returns {string} - Unit suffix
 */
export function getUnitSuffix(mode, interval, baseUnit = '') {
    const periodLabel = getPeriodLabel(interval);

    switch (mode) {
        case GRANULARITY_MODES.PER_SECOND:
            return baseUnit ? `${baseUnit}/s` : '/s';
        case GRANULARITY_MODES.PER_PERIOD:
            return baseUnit ? `${baseUnit}/${periodLabel}` : `/${periodLabel}`;
        case GRANULARITY_MODES.TOTAL:
        default:
            return baseUnit;
    }
}

/**
 * Transform an array of data points based on granularity mode
 * @param {Array} data - Array of data objects with numeric values
 * @param {string} mode - The granularity mode
 * @param {string} interval - The data interval
 * @param {Array<string>} fields - Fields to transform (leave empty for all numeric fields)
 * @returns {Array} - Transformed data array
 */
export function transformDataArray(data, mode, interval, fields = []) {
    if (!Array.isArray(data)) return data;

    return data.map(item => {
        const transformed = { ...item };
        const fieldsToTransform = fields.length > 0 ? fields : Object.keys(item);

        fieldsToTransform.forEach(field => {
            if (typeof item[field] === 'number') {
                transformed[field] = transformValue(item[field], mode, interval);
            }
        });

        return transformed;
    });
}

/**
 * Vue composable wrapper for granularity toggle functionality
 */
export function useGranularityToggle() {
    return {
        GRANULARITY_MODES,
        GRANULARITY_LABELS,
        GRANULARITY_SHORT_LABELS,
        getIntervalSeconds,
        getPeriodLabel,
        transformValue,
        getUnitSuffix,
        transformDataArray
    };
}

export default useGranularityToggle;
